import { z } from 'zod';

/**
 * Corporate website (ekho.com) events—marketing-site funnel and engagement
 * captured into the Ekho CDP (Segment → BigQuery + PostHog).
 *
 * Prefix `cw_` (corporate website); `co_` belongs to checkout. These are
 * CDP-only: they carry no `destinations`, so they never push to the dealer GTM
 * dataLayer or fire ad pixels. The corporate site's existing GTM container
 * (`GTM-58423FHZ`) owns the GA4/Meta fan-out under its own `ekho.*` dataLayer
 * names, independent of this registry. The corporate bootstrap forwards each
 * site event to both sinks: the untouched inline dataLayer push (GTM) and, via
 * these definitions, the CDP facade (PostHog + Segment). Keeping the two paths
 * separate is what guarantees zero regression to the ad-pixel tracking.
 */
/** Every event in this file originates on the marketing site. */
const SURFACE = 'corporate-website';
/**
 * The page the interaction happened on, named with the same taxonomy the page's
 * own `ViewContent` call uses. Carried on every engagement event so a click or a
 * scroll joins to the page view without reconstructing the session's page
 * sequence by timestamp.
 *
 * Optional because the value is read from the page's inline `ViewContent` call,
 * and a page that has not been given one yet would otherwise send a sentinel.
 * Absence means "this page declares no content name", which is a fixable site
 * bug rather than a missing measurement.
 */
const CONTENT_NAME = z
    .string()
    .optional()
    .describe('Display name of the page the interaction happened on. Absent when the page declares no ViewContent taxonomy.');
/**
 * Identifies one page view, minted client-side when the page starts tracking.
 *
 * Two jobs. It groups the several `cw_page_engaged` reports a single page view
 * produces, so a consumer can sum them back into one figure. And it separates
 * repeat views of the same page inside one session, which `content_name` alone
 * cannot: two visits to the pricing page are one label but two page views.
 */
const PAGE_VIEW_ID = z
    .string()
    .describe('Identifies one page view. Groups the engagement events belonging to it, and separates repeat views of the same page within a session.');
/**
 * Which Pit Stop subscribe surface the visitor used. Each surface reaches a
 * different reader at a different moment, so comparing them is the point of
 * reporting the subscribe as its own event rather than as a generic lead.
 */
const SIGNUP_SURFACE = z
    .enum([
    'newsletter_page',
    'newsletter_page_footer',
    'subscribe_section',
    'blog_header',
    'article_sidebar',
    'modal',
])
    .describe("The subscribe surface the visitor used: the /newsletter page hero or its closing card, the Pit Stop section on the homepage, blog index and articles, the compact blog header row, an article's sidebar, or the modal.");
const CORPORATE_EVENTS = {
    CW_PAGE_VIEWED: {
        canonical: 'cw_page_viewed',
        surface: SURFACE,
        description: 'A visitor loaded a marketing page on ekho.com.',
        schema: z.object({
            content_name: z.string().optional(),
            content_category: z.string().optional(),
            content_type: z.string().optional(),
        }),
    },
    CW_CONTACT_PAGE_VIEWED: {
        canonical: 'cw_contact_page_viewed',
        surface: SURFACE,
        description: 'A visitor loaded the demo or partnership contact page.',
    },
    CW_DEMO_BOOKED: {
        canonical: 'cw_demo_booked',
        surface: SURFACE,
        description: 'A visitor booked a demo meeting via the embedded HubSpot scheduler.',
    },
    CW_LEAD_SUBMITTED: {
        canonical: 'cw_lead_submitted',
        surface: SURFACE,
        description: 'A visitor completed a lead action (meeting booked or integration request).',
    },
    /**
     * A visitor subscribed to the Pit Stop newsletter and the subscribe endpoint
     * accepted the address, so the subscription now exists in HubSpot.
     *
     * The site also fires the historic `Lead` dataLayer event on the same
     * success, which keeps the GTM conversion as it is. This event is the one
     * that says which surface did the work.
     * @dedupe Once per accepted subscribe. The form hides on success, so the same surface cannot report twice in a page view.
     */
    CW_NEWSLETTER_SUBSCRIPTION_CREATED: {
        canonical: 'cw_newsletter_subscription_created',
        surface: SURFACE,
        description: 'A visitor subscribed to the Ekho Pit Stop newsletter and the subscribe endpoint accepted the address.',
        schema: z.object({
            signup_surface: SIGNUP_SURFACE,
        }),
    },
    /**
     * A Pit Stop subscribe attempt that did not go through. The reason is a
     * bounded value, never the address or the endpoint's own message.
     * @dedupe Intentionally un-deduped: each rejected press is a real attempt. Bounded by how often a visitor presses Subscribe.
     */
    CW_NEWSLETTER_SIGNUP_FAILED: {
        canonical: 'cw_newsletter_signup_failed',
        surface: SURFACE,
        description: 'A visitor pressed Subscribe on a Pit Stop surface and the subscribe was rejected, either in the browser or by the endpoint.',
        schema: z.object({
            signup_surface: SIGNUP_SURFACE,
            failure_reason: z
                .enum(['invalid_email', 'consumer_email', 'endpoint_rejected', 'network_error'])
                .describe('Why the subscribe failed: a malformed address, a free consumer email domain, a non-2xx response from the endpoint, or no response at all.'),
        }),
    },
    // ---------------------------------------------------------------------------
    // Engagement. The four events below answer what a visitor did on a page, which
    // the page-view events alone cannot. Before them ekho.com reported arrivals and
    // nothing else: no click was measurable, and 86% of sessions were a single page
    // view with no second timestamp, so time on page did not exist as a quantity.
    //
    // All four are CDP-only, like the rest of this file. They are emitted straight
    // through the analytics facade rather than through `window.ekhoTrack`, so the
    // GTM container and its ad fan-out are untouched.
    // ---------------------------------------------------------------------------
    /**
     * A press on a control that asks the visitor to convert.
     *
     * Deliberately not every link on the page. Convention 13 asks for instrumenting
     * on purpose, and an undifferentiated click stream would bury the handful of
     * presses that carry intent under navigation noise. A control qualifies by
     * carrying `data-cta`, or by pointing at a conversion destination.
     *
     * `cta_label` is the control's own visible text, trimmed and capped. It is
     * author-written UI copy rather than anything the visitor typed, so it carries
     * no PII, but it does drift when copy changes, and several controls on one page
     * legitimately share it. Group on `cta_id` where a placement declares one, and
     * on `destination` otherwise, when a report needs to survive a rewording.
     * @dedupe Intentionally un-deduped: pressing the same CTA twice is a real repeat. Capped at 5 reports per control per page view, since the repeat count saturates as signal long before it stops costing money.
     */
    CW_CTA_CLICKED: {
        canonical: 'cw_cta_clicked',
        surface: SURFACE,
        description: 'A visitor pressed a call-to-action on ekho.com: a control marked data-cta, or a link to a conversion destination such as the demo, pricing, or integration-request pages.',
        schema: z.object({
            cta_label: z
                .string()
                .describe('Visible text of the control, trimmed and capped at 100 characters.'),
            cta_location: z
                .enum(['nav', 'above_fold', 'body', 'footer'])
                .describe('Which region of the page the control sits in. `nav` and `footer` come from the nearest landmark element; `above_fold` means the control sat within the first viewport height at click time. Separates a nav CTA a visitor passes constantly from one they had to read the page to reach.'),
            destination: z
                .string()
                .optional()
                .describe('Path the control leads to, without host or query. Absent on a button that does not navigate, which has no destination to report.'),
            cta_id: z
                .string()
                .optional()
                .describe("Author-assigned identifier from the control's `data-cta` value, stable across copy changes. Absent on a control that qualifies by its conversion destination rather than by being marked, and on a bare `data-cta` with no value."),
            content_name: CONTENT_NAME,
            page_view_id: PAGE_VIEW_ID,
        }),
    },
    /**
     * A visitor reached a depth threshold on the page.
     *
     * Thresholds are the industry-standard quarters rather than GA4's lone 90%
     * default, because 90% alone cannot tell an article that lost its reader in the
     * first screen from one that nearly held them.
     *
     * A page that fits the viewport reports nothing at all. It has no depth to
     * cross, and scoring it as fully read would credit reading that never happened.
     * @dedupe Once per threshold per page view. Scrolling back up and down again does not re-report.
     */
    CW_SCROLL_DEPTH_REACHED: {
        canonical: 'cw_scroll_depth_reached',
        surface: SURFACE,
        description: 'A visitor scrolled past a depth threshold on an ekho.com page. Reported once per threshold per page view.',
        schema: z.object({
            depth_pct: z
                .union([z.literal(25), z.literal(50), z.literal(75), z.literal(90)])
                .describe('The threshold crossed, as a percentage of total scrollable page height.'),
            content_name: CONTENT_NAME,
            page_view_id: PAGE_VIEW_ID,
        }),
    },
    /**
     * How long a visitor was actually present on the page.
     *
     * This is the event that makes time measurable at all. It fires when the page
     * is hidden or unloaded, so it does not depend on a second page view, which is
     * what made duration unmeasurable for the 86% of sessions that never load one.
     *
     * `engaged_seconds` counts only foreground time. The timer stops when the tab
     * is hidden, so a tab left open overnight reports the seconds actually spent
     * reading rather than the wall-clock gap. That difference is the whole reason
     * the naive page-view-gap measure was not worth shipping: it averaged 72
     * minutes against a 75-second median.
     *
     * **It reports an increment, not a total.** One page view emits several of
     * these, so total time on a page is `SUM(engaged_seconds)` grouped by
     * `page_view_id`, and deepest scroll is `MAX(max_depth_pct)` over the same
     * group. A single terminal report cannot work: the only exit signal that
     * survives mobile Safari is the tab being hidden, and a visitor who comes back
     * goes on reading, so a once-per-page-view report would silently measure time
     * until the first switch away instead of time on the page.
     * @dedupe Intentionally un-deduped: each report covers a distinct stretch of foreground time, so reports are additive rather than repeats. Bounded by how often a visitor can background the page.
     */
    CW_PAGE_ENGAGED: {
        canonical: 'cw_page_engaged',
        surface: SURFACE,
        description: 'A visitor left or backgrounded an ekho.com page, reporting the foreground time they spent on it. The basis for time-on-page and for a real bounce rate.',
        schema: z.object({
            engaged_seconds: z
                .number()
                .int()
                .describe('Foreground seconds since this page view last reported, not a running total. Excludes time while the tab was hidden, so an abandoned open tab does not inflate it. Sum over page_view_id for time on page.'),
            max_depth_pct: z
                .number()
                .int()
                .optional()
                .describe('Deepest scroll depth reached so far, as a percentage of page height. Absent on a page that fits the viewport and so has no depth to report.'),
            content_name: CONTENT_NAME,
            page_view_id: PAGE_VIEW_ID,
        }),
    },
    /**
     * A visitor followed a link off ekho.com.
     *
     * Reports the destination host rather than the full URL's query string, which
     * can carry identifiers a visitor never chose to share with the CDP.
     * @dedupe Intentionally un-deduped: following the same link twice is a real repeat. Capped at 5 reports per destination per page view, on the same reasoning as the CTA cap.
     */
    CW_OUTBOUND_LINK_CLICKED: {
        canonical: 'cw_outbound_link_clicked',
        surface: SURFACE,
        description: 'A visitor clicked a link leading to a host other than ekho.com, for example a docs site, a social profile, or a partner.',
        schema: z.object({
            destination_host: z
                .string()
                .describe('Host of the outbound link. Host only, so no query-string identifiers are carried.'),
            destination_path: z.string().describe('Path of the outbound link, without host or query.'),
            link_text: z
                .string()
                .describe('Visible text of the link, trimmed and capped at 100 characters.'),
            content_name: CONTENT_NAME,
            page_view_id: PAGE_VIEW_ID,
        }),
    },
};

export { CORPORATE_EVENTS };
//# sourceMappingURL=corporate.js.map
